// SEO Configuration for all pages

export const defaultSEO = {
  title: 'Importerr - 100M+ Products. One Powerful B2B Buying Platform',
  description: 'Importerr.com is a global B2B sourcing platform offering 100M+ products, verified suppliers, competitive wholesale prices, and simplified procurement tools for retailers, D2C brands, and online sellers. Scale your business with reliable sourcing and integrated fulfillment.',
  keywords: 'dropshipping, alibaba products, wholesale, china sourcing, product sourcing, ecommerce, dropshipping services, importerr, alibaba sourcing, product import',
  image: '/images/importerr-logo.png',
  type: 'website',
};

export const pageSEO = {
  home: {
    title: 'Importerr – India’s Largest B2B Global Sourcing & Supply Platform',
    description: 'Discover 100M+ products and trusted global suppliers with Importerr—India’s most advanced B2B sourcing and fulfillment platform. Simplify bulk buying, lower costs, and scale your business with confidence.',
    keywords: 'dropshipping platform, online dropshipping, alibaba dropshipping, china dropshipping, product sourcing, wholesale suppliers, ecommerce solutions',
  },
  
  products: {
    title: 'Browse Products - Thousands of Quality Products | Importerr',
    description: 'Explore our extensive catalog of dropshipping products across multiple categories. Find the perfect products for your online store with competitive pricing and reliable quality.',
    keywords: 'dropshipping products, wholesale products, alibaba products, trending products, product catalog, ecommerce products',
  },

  productDetails: {
    title: '{productName} - The Ultimate B2B Catalog for Wholesale & Bulk Buying',
    description: '{productDescription}',
    keywords: '{productName}, dropship {productName}, wholesale {productName}, buy {productName}',
  },

  about: {
    title: 'About Importerr - 100M+ Wholesale B2B Catalog  for Indian Businesses',
    description: 'Learn about Importerr, your trusted partner for dropshipping services. We connect businesses with quality suppliers and provide comprehensive support for your ecommerce success.',
    keywords: 'about importerr, dropshipping company, ecommerce solutions, sourcing services, alibaba partner',
  },

  contact: {
    title: 'Contact Us - Get In Touch with Importerr Support',
    description: 'Have questions about our dropshipping services? Contact Importerr customer support team. We\'re here to help you succeed in your ecommerce journey.',
    keywords: 'contact importerr, customer support, dropshipping help, importerr support, get in touch',
  },

  pricing: {
    title: 'Pricing Plans - Affordable Dropshipping Solutions | Importerr',
    description: 'Explore Importerr\'s transparent pricing plans for dropshipping services. Choose the plan that fits your business needs with no hidden fees and flexible options.',
    keywords: 'dropshipping pricing, importerr plans, dropshipping costs, pricing tiers, subscription plans',
  },

  helpCenter: {
    title: 'Help Center - Dropshipping Guides & FAQs | Importerr',
    description: 'Find answers to common questions about dropshipping with Importerr. Browse our comprehensive guides, tutorials, and FAQs to maximize your success.',
    keywords: 'dropshipping help, importerr faq, dropshipping guide, help center, how to dropship',
  },

  beASeller: {
    title: 'Become a Seller - Join Importerr Marketplace',
    description: 'Join Importerr as a seller and reach thousands of dropshippers worldwide. Expand your business by listing your products on our trusted platform.',
    keywords: 'become a seller, supplier registration, join importerr, sell on importerr, supplier marketplace',
  },

  privacyPolicy: {
    title: 'Privacy Policy - How Importerr Protects Your Data',
    description: 'Read Importerr\'s privacy policy to understand how we collect, use, and protect your personal information. Your privacy and security are our top priorities.',
    keywords: 'privacy policy, data protection, user privacy, importerr privacy',
  },

  termsConditions: {
    title: 'Terms & Conditions - Importerr User Agreement',
    description: 'Review Importerr\'s terms and conditions. Understand your rights and responsibilities when using our dropshipping platform and services.',
    keywords: 'terms and conditions, user agreement, importerr terms, service terms',
  },

  shippingPolicy: {
    title: 'Shipping Policy - Delivery Information | Importerr',
    description: 'Learn about Importerr\'s shipping policies, delivery times, and shipping methods. We ensure fast and reliable delivery for all your dropshipping orders.',
    keywords: 'shipping policy, delivery information, shipping times, importerr shipping',
  },

  returnRefund: {
    title: 'Return & Refund Policy - Customer Protection | Importerr',
    description: 'Understand Importerr\'s return and refund policy. We stand behind the quality of our products and offer fair resolution for any issues.',
    keywords: 'return policy, refund policy, customer protection, returns and refunds',
  },

  howDropshippingWorks: {
    title: 'How Dropshipping Works - Complete Guide | Importerr',
    description: 'Learn how dropshipping works with Importerr. Discover the step-by-step process of starting and running a successful dropshipping business.',
    keywords: 'how dropshipping works, dropshipping guide, start dropshipping, dropshipping tutorial',
  },

  whyWorkWithUs: {
    title: 'Why Choose Importerr - Benefits of Our Dropshipping Services',
    description: 'Discover why thousands of businesses trust Importerr for their dropshipping needs. Quality products, competitive pricing, and exceptional support.',
    keywords: 'why importerr, dropshipping benefits, why choose us, importerr advantages',
  },

  sellingGuide: {
    title: 'Selling Guide - Tips for Dropshipping Success | Importerr',
    description: 'Master the art of dropshipping with Importerr\'s comprehensive selling guide. Learn proven strategies to grow your online store and increase sales.',
    keywords: 'selling guide, dropshipping tips, ecommerce guide, selling strategies, online selling',
  },

  cart: {
    title: 'Shopping Cart - Review Your Products | Importerr',
    description: 'Review your selected products and proceed to checkout. Secure ordering process with multiple payment options available.',
    keywords: 'shopping cart, checkout, order products, importerr cart',
    noindex: true, // Don't index cart pages
  },

  checkout: {
    title: 'Checkout - Complete Your Order | Importerr',
    description: 'Complete your order securely with Importerr. Fast and secure checkout process with multiple payment methods.',
    keywords: 'checkout, complete order, payment, secure checkout',
    noindex: true, // Don't index checkout pages
  },
};

// Function to generate dynamic category SEO
export const generateCategorySEO = (categoryName, categoryDescription) => {
  return {
    title: `${categoryName} - Browse ${categoryName} Products | Importerr`,
    description: categoryDescription || `Discover high-quality ${categoryName} products for dropshipping. Source directly from trusted suppliers with competitive pricing and fast shipping.`,
    keywords: `${categoryName}, ${categoryName} dropshipping, ${categoryName} wholesale, buy ${categoryName}, ${categoryName} suppliers`,
  };
};

// Function to generate dynamic product SEO
export const generateProductSEO = (product) => {
  const productName = product?.name || product?.title || 'Product';
  const productDescription = product?.description || product?.shortDescription || `High-quality ${productName} available for dropshipping with fast shipping and competitive pricing.`;
  const productPrice = product?.price || product?.salePrice;
  const productImages = product?.images || product?.imageUrls || [];
  const mainImage = Array.isArray(productImages) ? productImages[0] : productImages;

  return {
    title: `${productName} - Buy Wholesale & Dropship | Importerr`,
    description: productDescription.length > 160 ? productDescription.substring(0, 157) + '...' : productDescription,
    keywords: `${productName}, dropship ${productName}, wholesale ${productName}, buy ${productName}, ${productName} supplier`,
    image: mainImage,
    type: 'product',
    product: {
      name: productName,
      description: productDescription,
      price: productPrice,
      currency: product?.currency || 'INR',
      images: Array.isArray(productImages) ? productImages : [mainImage],
      inStock: product?.inStock !== false,
      brand: product?.brand || 'Importerr',
      category: product?.category || product?.categoryName,
      rating: product?.rating,
      reviewCount: product?.reviewCount,
    },
  };
};

// Function to generate search results SEO
export const generateSearchSEO = (searchQuery, resultCount) => {
  return {
    title: `Search Results for "${searchQuery}" - ${resultCount} Products Found | Importerr`,
    description: `Browse ${resultCount} products matching "${searchQuery}". Find the perfect dropshipping products with competitive pricing and quality assurance.`,
    keywords: `${searchQuery}, search ${searchQuery}, ${searchQuery} products, buy ${searchQuery}`,
    noindex: true, // Don't index search result pages
  };
};

export default {
  defaultSEO,
  pageSEO,
  generateCategorySEO,
  generateProductSEO,
  generateSearchSEO,
};

